import {Spinner, useEnvironment} from "@keycloak/keycloak-ui-shared";

import { useState } from "react";
import { useTranslation } from "react-i18next";

import { deleteSession, getDevices } from "../../api/methods";
import {
  DeviceRepresentation,
  SessionRepresentation,
} from "../../api/representations";
import { Page } from "../../components/page/Page";
import { useAccountAlerts } from "../../utils/useAccountAlerts";
import { usePromise } from "../../utils/usePromise";
import {Environment} from "../../environment";
import Card from "../../components/card/Card";
import { Box, Divider } from "@mui/material";
import Grid from '@mui/material/Grid2';
import Button from "../../components/button/Button";
import ReloadIcon from "../../icons/Reload";
import SignOutIcon from "../../icons/SignOut";
import Modal from "../../components/modal/Modal";
import { withDivider } from "../../hoc/withDivider";
import { deviceCardActionStyles, deviceSignOutAllStyles } from "./styles";
import ActiveSessionRow from "./components/ActiveSessionRow";
import React from "react";

export const moveCurrentToTop = (fn: (devices: DeviceRepresentation[]) => void) => {

  return (devices: DeviceRepresentation[]) => {
      let currentDevice = devices[0];

      const index = devices.findIndex((d) => d.current);
      currentDevice = devices.splice(index, 1)[0];
      devices.unshift(currentDevice);

      const sessionIndex = currentDevice.sessions.findIndex((s) => s.current);
      const currentSession = currentDevice.sessions.splice(sessionIndex, 1)[0];
      currentDevice.sessions.unshift(currentSession);

      fn(devices);
    }
  };

export const DeviceActivity = () => {
  const { t } = useTranslation();
  const context = useEnvironment<Environment>();
  const { addAlert, addError } = useAccountAlerts();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [devices, setDevices] = useState<DeviceRepresentation[]>();
  const [key, setKey] = useState(0);
  const refresh = () => setKey(key + 1);

  usePromise((signal) => getDevices({ signal, context }), moveCurrentToTop(setDevices), [
    key,
  ]);

  const signOutAll = async () => {
    await deleteSession(context);
    context.keycloak.logout();
  };

  const signOutSession = async (
    session: SessionRepresentation,
    device: DeviceRepresentation,
  ) => {
    try {
      await deleteSession(context, session.id);
      addAlert(
        t("signedOutSession", { browser: session.browser, os: device.os }),
      );
      refresh();
    } catch (error) {
      addError("errorSignOutMessage", error);
    }
  };

  if (!devices) {
    return <Spinner />;
  }

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const onConfirm = () => {
    signOutAll();
    setIsModalOpen(false);
  };

  return (
    <Page
      title={t("deviceActivity")}
    >
      <Card title={t("signedInDevices")} action={
        <Box sx={deviceCardActionStyles}>
          <Button
            size="small"
            variant="outlined"
            onClick={() => refresh()}
            startIcon={<ReloadIcon />}
          >
            {t("refresh")}
          </Button>
          {(devices.length > 1 || devices[0].sessions.length > 1) && (<Button
            size="small"
            variant="contained"
            onClick={() => setIsModalOpen(true)}
            startIcon={<SignOutIcon sx={deviceSignOutAllStyles} />}
          >
            {t("signOutAllDevices")}
          </Button>)}
        </Box>
      } withHeadBorder>

        <Grid container aria-label={t("signedInDevices")} flexDirection="column">
          {devices.map((device, i, array) => (
            <React.Fragment key={device.id}>
              {
                device.sessions.map((session, indx) => (
                  withDivider(ActiveSessionRow)({
                    t,
                    items: device.sessions,
                    device,
                    session,
                    resourceUrl: context.environment.resourceUrl,
                    index: indx,
                    signOutSession,
                    showDivider: indx < device.sessions.length
                  })
                ))
              }
              {i < array.length - 1 && <Divider />}
            </React.Fragment>
          )
            
          )}
        </Grid>
      </Card>
      <Modal
        isOpen={isModalOpen}
        onClose={handleModalClose}
        title={t("signOutAllDevices")}
        confirmText={t("confirm")}
        onConfirm={onConfirm}
      >
        {t("signOutAllDevicesWarning")}
      </Modal>
    </Page>
  );
};

export default DeviceActivity;
